import { useMemo, useContext } from 'react';
import { ThemeContext } from '../../context/ThemeContext';

/**
* Renders a themed span that displays how long ago a given date occurred.
* @example
* TimeAgo({ date: '2025-11-23T12:00:00Z', className: 'timestamp' })
* <span className="timestamp" style={{ color: theme.text.tertiary }}>just now</span>
* @param {{Date|string}} {{date}} - The date or date string to compare against the current time.
* @param {{string}} {{className}} - Optional CSS class name applied to the span.
* @returns {{JSX.Element}} A span element showing the relative time elapsed (e.g., "2 hours ago").
**/
const TimeAgo = ({ date, className = '' }) => {
  const { theme } = useContext(ThemeContext);
  
  const timeAgo = useMemo(() => {
    if (!date) return '';
    
    const now = new Date();
    const past = new Date(date);
    const diffInSeconds = Math.floor((now - past) / 1000);
    
    if (diffInSeconds < 60) {
      return 'just now';
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    }
    
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) {
      return `${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`;
    }
    
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;
    }
    
    const diffInYears = Math.floor(diffInDays / 365);
    return `${diffInYears} year${diffInYears > 1 ? 's' : ''} ago`;
  }, [date]);

  return (
    <span className={className} style={{ color: theme.text.tertiary }}>
      {timeAgo}
    </span>
  );
};

export default TimeAgo;
